import React, { useState } from 'react';
import axios from 'axios';
import {
  Box, Paper, Typography, Button, Stack, CircularProgress, Alert,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Chip, Divider, Card, CardContent
} from '@mui/material';
import { Download, Refresh, CheckCircle, ErrorOutline, SkipNext } from '@mui/icons-material';

const API_URL = process.env.REACT_APP_API_URL || '/api';

const ImportEtudiants = () => {
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState('');

  const handleImport = async () => {
    try {
      setLoading(true);
      setError('');
      setResult(null);
      const token = localStorage.getItem('token');
      const res = await axios.post(`${API_URL}/users/import-etudiants`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setResult(res.data);
    } catch (err) {
      setError(err.response?.data?.error || err.message);
    } finally {
      setLoading(false);
    }
  };

  const downloadJSON = () => {
    if (!result?.created?.length) return;
    const data = result.created.map(u => ({
      nom: u.nom,
      prenom: u.prenom,
      email: u.email,
      password: u.password,
      role: 'ETUDIANT'
    }));
    const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'comptes_etudiants.json';
    a.click();
    URL.revokeObjectURL(url);
  };

  const downloadCSV = () => {
    if (!result?.created?.length) return;
    let csv = 'Nom;Prénom;Email;Mot de passe\n';
    result.created.forEach(u => {
      csv += `${u.nom};${u.prenom};${u.email};${u.password}\n`;
    });
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'comptes_etudiants.csv';
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" fontWeight="bold" sx={{ mb: 1 }}>
        Import Utilisateurs Étudiants
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Crée automatiquement un compte utilisateur pour chaque étudiant actif n'en possédant pas encore.
        Format email : <strong>prenom.nom@sup2i.ac</strong> — mot de passe généré aléatoirement.
        L'étudiant devra changer son mot de passe à la première connexion.
      </Typography>

      <Stack direction="row" spacing={2} sx={{ mb: 3 }}>
        <Button
          variant="contained"
          size="large"
          onClick={handleImport}
          disabled={loading}
          startIcon={loading ? <CircularProgress size={20} /> : <Refresh />}
        >
          {loading ? 'Création en cours...' : 'Lancer l\'import'}
        </Button>
      </Stack>

      {error && <Alert severity="error" sx={{ mb: 3 }}>{error}</Alert>}

      {result && (
        <>
          <Stack direction="row" spacing={2} sx={{ mb: 3 }}>
            <Chip icon={<CheckCircle />} label={`${result.created?.length || 0} créé(s)`} color="success" />
            <Chip icon={<SkipNext />} label={`${result.skipped?.length || 0} ignoré(s)`} color="default" />
            <Chip icon={<ErrorOutline />} label={`${result.errors?.length || 0} erreur(s)`} color="error" />
            <Chip label={`${result.total || 0} étudiant(s) au total`} variant="outlined" />
          </Stack>

          {result.created?.length > 0 && (
            <>
              <Stack direction="row" spacing={2} sx={{ mb: 2 }}>
                <Button variant="outlined" startIcon={<Download />} onClick={downloadCSV}>
                  Télécharger CSV
                </Button>
                <Button variant="outlined" startIcon={<Download />} onClick={downloadJSON}>
                  Télécharger JSON
                </Button>
              </Stack>

              <TableContainer component={Paper} sx={{ borderRadius: 2, mb: 3 }}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Nom</TableCell>
                      <TableCell>Prénom</TableCell>
                      <TableCell>Email</TableCell>
                      <TableCell>Mot de passe</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {result.created.map((u, i) => (
                      <TableRow key={i}>
                        <TableCell>{u.nom}</TableCell>
                        <TableCell>{u.prenom}</TableCell>
                        <TableCell>{u.email}</TableCell>
                        <TableCell>
                          <Chip label={u.password} size="small" variant="outlined" />
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </>
          )}

          {result.errors?.length > 0 && (
            <Paper sx={{ p: 2, borderRadius: 2, mb: 2 }}>
              <Typography variant="subtitle2" color="error" sx={{ mb: 1 }}>Erreurs</Typography>
              {result.errors.map((e, i) => (
                <Typography key={i} variant="caption" display="block">
                  {e.nom} {e.prenom} ({e.cne}) : {e.erreur}
                </Typography>
              ))}
            </Paper>
          )}

          {result.skipped?.length > 0 && (
            <Paper sx={{ p: 2, borderRadius: 2 }}>
              <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>Ignorés (déjà existants)</Typography>
              {result.skipped.map((s, i) => (
                <Typography key={i} variant="caption" display="block">
                  {s.nom} {s.prenom} ({s.cne})
                </Typography>
              ))}
            </Paper>
          )}
        </>
      )}
    </Box>
  );
};

export default ImportEtudiants;


